import type { Translation } from '@/types/translation';

// Dutch (NL) translation file.

const nlTranslation: Translation = {
  common: {
    save: 'Opslaan',
    close: 'Sluiten',
    search: 'Zoeken',
    back: 'Terug',
    continue: 'Doorgaan',
    cancel: 'Annuleren',
    submit: 'Verzenden',
    confirm: 'Bevestigen',
    delete: 'Verwijderen',
    save_changes: 'Wijzigingen opslaan',
    loading: 'Laden...',
  },
  pages: {
    account: {
      tabs: {
        dashboard: 'Dashboard',
        fleet_overview: 'Vlootoverzicht',
        unit_overview: 'Alle units',
        campaigns: 'Campagnes',
        members: 'Leden',
      },
      dashboard: {
        active_fault_codes: {
          title: 'Actieve foutcodes',
          units_title: 'Units met actieve foutcodes',
        },
        connectivity: {
          title: 'Connectiviteit',
          units_title: 'Connectiviteitsstatus',
        },
        fuel_consumption: {
          title: 'Brandstofverbruik',
          details_title: 'Brandstofverbruik details',
        },
      },
      fleet_overview: {
        pinned_fleets: 'Vastgezette vloten',
        other_fleets: 'Andere vloten',
        pin: 'Vloot vastzetten',
        unpin: 'Vloot losmaken',
        edit: 'Vloot bewerken',
        remove: 'Vloot verwijderen',
      },
      unit_overview: {
        title: 'Units',
        unverified_ownership: 'Onverifieerd eigendom',
        add_to_fleet: 'Toevoegen aan vloot',
      },
      campaigns: {
        active_table: {
          title: 'Actieve campagnes',
          download_campaigns: 'Campagnes downloaden (xlsx)',
          empty_state: {
            title: 'Je hebt geen actieve campagnes voor deze unit',
          },
        },
        performed_table: {
          title: 'Uitgevoerde campagnes',
          empty_state: {
            title: 'Geen uitgevoerde campagnes beschikbaar',
          },
        },
      },
      members: {
        title: 'Leden',
        remove_members_from_account: 'Verwijder {{selectedRows}} leden uit account',
      },
    },
    fleet: {
      tabs: {
        dashboard: 'Dashboard',
        unit_overview: 'Alle units',
        campaigns: 'Campagnes',
      },
      campaigns: {
        title: 'Actieve campagnes',
        download_campaigns: 'Campagnes downloaden (xlsx)',
        empty_state: {
          title: 'Je hebt geen actieve campagnes voor deze vloot',
        },
      },
    },
    unit: {
      tabs: {
        specifications: 'Specificaties',
        campaigns: 'Campagnes',
        coverage: 'Garantie & dekking',
        maintenance_history: 'Onderhoudsgeschiedenis',
        manuals_handbooks: 'Handleidingen & handboeken',
        diagnostics_log: 'Diagnose logboek',
        services: 'Digitale diensten',
        settings: 'Instellingen',
      },
      spare_parts: 'Reserveonderdelen',
      manuals_selection_description: 'Voor welk product wilt u handleidingen en handboeken vinden.',
      specifications: {
        title: 'Specificaties',
      },
      campaigns: {
        active_table: {
          title: 'Actieve campagnes',
          empty_state: {
            title: 'Je hebt geen actieve campagnes voor deze unit',
          },
        },
        performed_table: {
          title: 'Uitgevoerde campagnes',
          empty_state: {
            title: 'Geen uitgevoerde campagnes beschikbaar',
          },
        },
      },
      coverage: {
        cta: {
          heading: 'Informatie over garantie & dekking',
          message: 'Voor vragen over garantie & dekking neemt u contact op met uw Volvo Penta- of OEM-dealer.',
          find_dealer: 'Zoek uw dichtstbijzijnde dealer',
          learn_more: 'Meer informatie',
        },
        empty_state: {
          title: 'Geen garantie en/of dekking gevonden voor deze unit',
        },
      },
      publications: {
        select_menu: {
          category: 'Categorie',
          designation: 'Aanduiding',
          product_type: 'Producttype',
          language: 'Taal',
        },
        empty_state: {
          title: 'Geen publicaties beschikbaar voor deze unit',
        },
      },
      services: {
        title: 'Beschikbare diensten',
        all_services: {
          title: 'Activeer alle services',
        },
        list: {
          ActiveMonitoring: {
            Marine: {
              title: 'Extended',
              description:
                'Krijg alle voordelen van Core plus automatische e-mailmeldingen die rechtstreeks naar uw geselecteerde dealer worden verzonden.',
              checklist: [
                'Waarschuw uw dealer direct als uw systeem een kritieke foutcode detecteert',
                'Stel uw dealer in staat om onmiddellijke corrigerende maatregelen voor te bereiden',
              ],
            },
            Industrial: {
              title: 'Actieve monitoring',
              description:
                'Active Monitoring helpt klanten hun Volvo Penta-producten draaiende te houden door problemen vroegtijdig te detecteren, snellere service te ondersteunen en duidelijk inzicht te geven in de status en het gebruik van het product.\n\nActive Monitoring maakt ook Remote Software Updates mogelijk, een service om softwarecampagnes over-the-air te installeren wanneer het u uitkomt.',
              checklist: [
                'Hogere uptime dankzij vroegtijdige waarschuwingen en proactieve ondersteuning',
                'Snellere, beter voorbereide service doordat de juiste informatie vooraf beschikbaar is',
              ],
            },
          },
          ActiveMonitoringCore: {
            Marine: {
              title: 'Core',
              description:
                'Deel basisvoertuiggegevens met uw dealer voor snellere ondersteuning bij onderhoud of onverwachte problemen.',
              checklist: [
                'Laat uw dealer de productstatus bewaken en bedrijfsuren bijhouden',
                'Laat uw dealer foutcodes direct analyseren voor proactieve serviceplanning',
              ],
            },
            Industrial: {
              title: 'Connected Services Core',
              description:
                'Core stelt de Volvo Penta-dealer in staat om het Volvo Penta-product te monitoren. <ActiveMonitoringCore>Voor verdere details, zie de servicebeschrijving voor Connected Services Core.</ActiveMonitoringCore>',
              checklist: [],
            },
          },
          ActiveMonitoringEcWifi: {
            Marine: {
              title: 'Extended',
              description:
                'Stel uw dealer in staat om voertuiggegevens te volgen en direct meldingen te ontvangen bij kritieke foutcodes om onmiddellijke corrigerende maatregelen voor te bereiden.',
              checklist: [
                'Waarschuw uw dealer direct als uw systeem een kritieke foutcode detecteert',
                'Stel uw dealer in staat om onmiddellijke corrigerende maatregelen voor te bereiden',
              ],
            },
            Industrial: {
              title: 'Extended',
              description:
                'Stel uw dealer in staat om voertuiggegevens te volgen en direct meldingen te ontvangen bij kritieke foutcodes om onmiddellijke corrigerende maatregelen voor te bereiden.',
              checklist: [
                'Waarschuw uw dealer direct als uw systeem een kritieke foutcode detecteert',
                'Stel uw dealer in staat om onmiddellijke corrigerende maatregelen voor te bereiden',
              ],
            },
          },
          Penta_Position: {
            Marine: {
              title: 'Locatieservices',
              description:
                'Location Services helpt klanten hun verbonden Volvo Penta-producten snel te lokaliseren en te beheren via een kaartweergave in Volvo Penta Connect, wat servicebeheer en vlootoverzicht eenvoudiger maakt.',
              checklist: [
                'Beter vlootoverzicht met een kaart die de laatste locatie van verbonden units toont',
                'Snellere servicerespons door eenvoudige toegang tot de locatie van de unit, inclusief bedrijfsuren en foutcodes',
              ],
            },
            Industrial: {
              title: 'Locatieservices',
              description:
                'Location Services helpt klanten hun verbonden Volvo Penta-producten snel te lokaliseren en te beheren via een kaartweergave in Volvo Penta Connect, wat servicebeheer en vlootoverzicht eenvoudiger maakt.',
              checklist: [
                'Beter vlootoverzicht met een kaart die de laatste locatie van verbonden units toont',
                'Snellere servicerespons door eenvoudige toegang tot de locatie van de unit, inclusief bedrijfsuren en foutcodes',
              ],
            },
          },
        },
        request_sent: 'Aanvraag verzonden',
        empty_state: {
          title: 'Serviceprovisioning niet beschikbaar',
          message: 'Digitale serviceprovisioning is niet beschikbaar voor deze eenheid.',
        },
        active_services_title: 'Actieve services',
        available_services_title: 'Beschikbare services',
        activate_button: 'Service activeren',
        terminate_button: 'Beëindigen',
        read_more_button: 'Lees meer',
        requires_prefix: 'Vereist {{serviceName}}',
      },
      settings: {
        unit_settings: {
          title: 'Unitinstellingen',
          unit_name: 'Unitnaam',
          home_port: 'Thuishaven',
          operating_location: 'Operationele locatie',
          upload_image: 'Afbeelding uploaden',
          add_files: 'Bestanden toevoegen',
          helperText: 'Png-, .jpg-bestand van max. 3 MB',
        },
        network_settings: {
          title: 'Netwerkinstellingen',
          empty_state: {
            title: 'Geen actieve netwerken beschikbaar',
            message: 'De netwerkinstellingen voor deze unit worden hier weergegeven zodra ze zijn geconfigureerd.',
          },
          updating_title: 'Netwerkinstellingen worden bijgewerkt',
          updating_description: 'Even geduld terwijl we de laatste netwerkconfiguratie ophalen.',
        },
      },
      maintenance: {
        service_protocol_for_chassi: 'Serviceprotocol voor chassis {{chassisNumber}}',
        download_pdf: 'Download PDF',
        service: 'Service {{type}}',
        default_service: 'Aandrijflijn {{chassisSeries}} {{chassisNumber}}',
        general_comment: 'Algemene serviceopmerking',
        no_comment: 'Geen algemene opmerkingen voor dit serviceprotocol.',
        periodic_tag: 'Elke {{months}} maand / {{engineHours}} h',
        non_periodic_month_tag: 'Op {{months}} maand',
        non_periodic_hours_tag: 'Op {{engineHours}} h',
        empty_state: {
          title: 'Geen onderhoudsgeschiedenis beschikbaar',
          message:
            'Serviceprotocollen voor deze motor verschijnen hier zodra ze zijn geregistreerd door een Volvo Penta-dealer.',
        },
        unverified_ownership_notice:
          'Sommige informatie kan beperkt zijn omdat het eigendom van dit apparaat nog niet is geverifieerd. Neem contact op met uw erkende Volvo Penta-dealer om de verificatie te voltooien.',
      },
    },
    settings: {
      application_settings: 'Applicatie-instellingen',
      account_name: 'Naam vlootaccount',
      notifications: 'Meldingen',
      email: 'E-mail',
      email_notifications: 'E-mailmeldingen inschakelen',
      sms: 'Sms',
      sms_notifications: 'Sms-meldingen inschakelen',
    },
    support: {
      title: 'Veelgestelde vragen',
      description: 'Vind contactinformatie en algemene informatie over Volvo Penta Connect.',
      professional_support: {
        tag: 'Professionele ondersteuning',
        title: 'De ultieme experts - tot uw dienst',
        description:
          'Neem contact op met een erkende Volvo Penta-dealer als eerste aanspreekpunt wanneer u hulp nodig heeft bij motorservice, reparatie, onderdelen of andere ondersteuning - voor zowel recreatieve als commerciële vaartuigen.',
      },
      emergency_support: {
        tag: 'Noodondersteuning',
        title: 'Volvo Penta Action Service',
        description:
          'Een motorstoring gebeurt zelden, maar als het gebeurt, staat Action Service klaar om u in contact te brengen met de dichtstbijzijnde Volvo Penta-partner. We bieden ondersteuning in meerdere talen, 24 uur per dag, het hele jaar door.',
      },
      platform_support: {
        tag: 'Platformondersteuning',
        title: 'Volvo Penta Connect Ondersteuning',
        description:
          'Neem contact op met ons toegewijde team voor hulp bij uw Volvo Penta Connect-account en functies. We zijn er om u te helpen uw motorgegevens en connectiviteit eenvoudig te beheren.',
      },
      getting_started: {
        title: 'Aan de slag',
        question_1: {
          title: 'Voor wie is Volvo Penta Connect?',
          body: 'Volvo Penta Connect is een klantenportaal dat u een overzicht geeft van uw unit(s) en toegang tot relevante informatie zoals handleidingen en gidsen, campagnes, garantstatus en meer.',
        },
        question_2: {
          title: 'Wat moet ik eerst doen om dit product te gebruiken?',
          body: 'U moet een unit registreren bij uw account. Zodra een unit is toegevoegd, kunt u unitgegevens monitoren, over-the-air software-updates installeren en toegang krijgen tot handleidingen en gidsen die aan die unit zijn gekoppeld.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Unit toevoegen' },
        },
        question_3: {
          title: 'Kan ik dit product gebruiken zonder een unit toe te voegen?',
          body: 'Nee. Volvo Penta Connect is gebouwd rond uw units. Handleidingen, motorgegevens en digitale diensten zijn allemaal gekoppeld aan een specifieke unit. Als u geen unit bezit of van plan bent een unit te registreren, voldoet dit product niet aan uw behoeften.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Unit toevoegen' },
        },
        question_4: {
          title: 'Hoe registreer ik mijn motor?',
          body: 'Ga naar de lijst met units en selecteer Unit toevoegen. Zoek naar het serienummer en voltooi vervolgens het registratieformulier. Sommige nieuwere industriële motoren worden geleverd met een vooraf geïnstalleerde QR-code op de motor. Indien beschikbaar, kunt u deze code scannen om het registratieproces te starten.',
        },
        question_5: {
          title: 'Welke informatie moet ik verstrekken?',
          body: 'Begin met het registreren van uw unit in Volvo Penta Connect. Ga naar de lijst met units en selecteer Unit toevoegen, zoek vervolgens naar het serienummer van de unit en voltooi het registratieformulier.<br /><br />Zodra de unit is geregistreerd, krijgt u toegang tot informatie zoals handleidingen, reserveonderdelen, campagnes, garantstatus en digitale diensten die aan die unit zijn gekoppeld.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Unit toevoegen' },
        },
        question_6: {
          title: 'Wat kan ik doen zodra mijn unit is geregistreerd?',
          body: 'Met een geregistreerde unit kunt u:<br /><br /><ul><li>- Units groeperen in fleets op basis van voertuigtype of gebruiksscenario</li><li>- Unitstatus monitoren</li><li>- Over-the-air software-updates installeren</li><li>- Toegang krijgen tot handleidingen en gidsen die specifiek zijn voor uw unit</li></ul>',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Unitoverzicht' },
        },
        question_7: {
          title: 'Waar kan ik handleidingen & gidsen vinden?',
          body: 'Handleidingen en gidsen worden beschikbaar zodra een unit aan uw account is toegevoegd, zodat we documentatie kunnen tonen die overeenkomt met uw specifieke unit.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Unit toevoegen' },
        },
        question_8: {
          title: 'Hoe voeg ik een gebruiker toe aan mijn fleet-account?',
          body: 'Om een nieuwe gebruiker toe te voegen, moet u deze uitnodigen vanaf de pagina Leden.<br /><br /><ul><li>1. Ga naar Leden</li><li>2. Selecteer Lid toevoegen</li><li>3. Maak een uitnodigingslink en kopieer deze</li><li>4. Deel de link met de persoon die u wilt uitnodigen</li><li>5. Zodra de uitnodiging is geaccepteerd, wijs een rol toe aan het nieuwe lid</li></ul><br/>Alleen gebruikers met toestemming om leden te beheren kunnen uitnodigingen verzenden en rollen toewijzen.',
          link: { href: 'https://www.volvopenta.com/connect/members', label: 'Leden' },
        },
        question_9: {
          title: "Waarom wordt mijn unit gemarkeerd als 'Ongeverifieerde eigenaar'?",
          body: 'Nieuw geregistreerde units worden tijdelijk gemarkeerd als Ongeverifieerde eigenaar totdat het eigendom is bevestigd.<br /><br />Het eigendom kan worden geverifieerd door een erkende Volvo Penta-dealer of door ons ondersteuningsteam. Zodra het is geverifieerd, wordt de tag automatisch verwijderd.<br /><br />Als u hulp nodig heeft bij de verificatie, neem dan contact op met uw erkende Volvo Penta-dealer.',
        },
        question_10: {
          title: 'Waarom kan ik mijn units niet zien?',
          body: "Uw units worden mogelijk om verschillende redenen niet in uw account weergegeven. Controleer deze veelvoorkomende scenario's:<br /><br />-mail mismatch:<br/>Uw units zijn gekoppeld aan een specifiek e-mailadres. Als u een account hebt aangemaakt met een ander adres, zijn de units niet zichtbaar. U kunt een nieuw account aanmaken met het juiste e-mailadres of contact opnemen met een erkende Volvo Penta-dealer om uw unit opnieuw te registreren.<br /><br />In afwachting van registratie:<br/>Als u een nieuwe industriële unit hebt toegevoegd, staat deze mogelijk nog niet in onze administratie. Deze units worden pas weergegeven in Volvo Penta Connect nadat ons ondersteuningsteam het handmatige registratieproces heeft voltooid.<br /><br />Accountrollen:<br/>Uw toegang tot specifieke units hangt af van uw toegewezen rol. Als u denkt dat uw toegang beperkt is, neem dan contact op met uw fleetmanager om ervoor te zorgen dat u de juiste accountrol heeft.",
        },
      },
      connectivity_telematics: {
        title: 'Connectiviteit & telematica',
        question_1: {
          title: 'Hoe weet ik of mijn unit is verbonden?',
          body: 'U kunt de verbindingsstatus van een unit zien naast de naam in de unitlijst en op de unitpagina.<br /><br /><GreenDot /> – De unit is online. Gegevens zijn binnen de laatste 2 uur ontvangen.<br /><br /><GreyDot /> – De unit is offline. Er zijn geen gegevens ontvangen in de laatste 2 uur.<br /><br /><YellowDot /> – Connectiviteit is beschikbaar maar niet volledig ingesteld.<br /><br />Geen stip – De unit heeft geen connectiviteitsmogelijkheid. Het kan in aanmerking komen voor een installatie van een telematica-gateway (TGW) via een dealer.<br /><br />Als uw unit offline is, kan deze nog steeds normaal worden gebruikt, maar gegevensupdates worden hervat zodra de unit opnieuw verbinding maakt.',
        },
        question_2: {
          title: 'Waarom wordt de verbindingsstatus van mijn unit niet weergegeven?',
          body: 'Units zonder statusindicator hebben geen telematica-gateway (TGW) geïnstalleerd of geactiveerd. Deze units kunnen worden verbonden door een TGW te installeren via een Volvo Penta-dealer.',
        },
        question_3: {
          title: 'Wat is een telematica-gateway (TGW)?',
          body: 'Een telematica-gateway (TGW) is een hardware-apparaat dat op de unit wordt geïnstalleerd en connectiviteit en gegevensoverdracht naar Volvo Penta Connect mogelijk maakt.<br /><br />Zonder een TGW kan de unit niet digitaal worden gemonitord.',
        },
        question_4: {
          title: 'Hoe verbind ik mijn unit met Volvo Penta Connect?',
          body: 'Om een unit te verbinden, moeten de volgende stappen worden voltooid:<br /><br /><ul><li>- De unit moet een TGW hebben geïnstalleerd</li><li>- De TGW moet worden geactiveerd door een dealer of OEM</li><li>- Vereiste digitale diensten moeten worden ingeschakeld</li><li>- De eigenaar moet akkoord gaan met de gegevensbeheerovereenkomst</li></ul><br /><br />Zodra dit is voltooid, verschijnt de unit met verbindingsstatus in Volvo Penta Connect.',
        },
        question_5: {
          title: 'Welke smartphones/tablets zijn compatibel met de Volvo Penta Connect-app?',
          body: 'Voor iOS-apparaten moet de besturingssysteemversie iOS 17 of later zijn. Voor Android-apparaten moet de besturingssysteemversie 9.0 of later zijn.',
        },
        question_6: {
          title: 'Wie kan mij helpen bij het installeren of activeren van connectiviteit?',
          body: 'Een Volvo Penta-dealer kan helpen met de installatie, activatie en service-instellingen van de TGW.',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Vind een Volvo Penta-dealer',
          },
        },
        question_7: {
          title: 'Hoe stel ik Wi-Fi in voor mijn unit?',
          body: 'U kunt uw Wi-Fi-verbinding rechtstreeks in de applicatie configureren. Volg deze stappen om te beginnen:<br /><br /><ul><li>- **Navigeer naar instellingen:** Ga naar het Wi-Fi-configuratiegedeelte in uw dashboard.</li><li>- **Volg de handleiding:** Gebruik het instructieboek (PDF) voor stapsgewijze aanwijzingen om uw configuratie te voltooien.</li><li>- **Verbind uw unit:** Eenmaal geconfigureerd, blijft uw unit automatisch verbonden met uw digitale werkruimte.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/connect/documents/Connect-to-WiFi-onboard.pdf',
            label: 'Instructiehandleiding',
            icon: 'import',
          },
        },
      },
      campaigns_software_updates: {
        title: 'Campagnes & software-updates',
        question_1: {
          title: 'Wat is een campagne?',
          body: 'Een campagne is een actie die door Volvo Penta wordt geïnitieerd om de productkwaliteit te waarborgen. Als een campagne van invloed is op een van uw geregistreerde units, wordt u op de hoogte gebracht en door de vereiste stappen geleid.',
          link: {
            href: 'https://www.volvopenta.com/connect/campaigns',
            label: 'Overzicht van campagnes',
          },
        },
        question_2: {
          title: 'Wat moet ik doen als ik een actieve campagne heb?',
          body: 'Het hangt af van het type campagne:<br /><br /><ul><li>- **Terugroepactie / Kwaliteit / Service:** Neem contact op met uw lokale Volvo Penta-dealer</li><li>- **Installatie / OTA:** Deze campagnes kunnen via de lucht worden voltooid met behulp van de Volvo Penta-app. Download de app en volg de instructies in de app.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Vind uw dealer',
          },
          ios: { href: '#', label: 'App store' },
          android: { href: '#', label: 'Google play' },
        },
        question_3: {
          title: 'Wat heb ik nodig voordat ik een over-the-air (OTA) campagne installeer?',
          body: 'Voordat u een OTA-installatie start, moet u ervoor zorgen dat:<br /><br /><ul><li>- U de juiste unit hebt geselecteerd</li><li>- Bluetooth is ingeschakeld op uw apparaat</li><li>- U zich binnen 3 meter van de unit bevindt</li><li>- Alle vereiste voorwaarden die in het product worden weergegeven, zijn voldaan</li><li>- De unit blijft gedurende de installatie van stroom voorzien en onaangetast</li></ul><br/>Als aan een van de vereisten niet wordt voldaan, start de installatie niet.',
        },
        question_4: {
          title: 'Ik ondervind problemen bij het installeren van een over-the-air (OTA) campagne.',
          body: 'Als de installatie mislukt of niet start:<br /><br /><ul><li>- Controleer of Bluetooth is ingeschakeld en u zich dicht bij de unit bevindt</li><li>- Blijf gedurende de hele installatie binnen 3 meter van de unit</li><li>- Start de app opnieuw en probeer het opnieuw</li><li>- Controleer opnieuw of aan alle vereisten van de unit is voldaan</li></ul><br/>Als het probleem aanhoudt, neem dan contact op met de ondersteuning.',
        },
        question_5: {
          title: 'Waar kan ik mijn voltooide over-the-air (OTA) installaties zien?',
          body: 'U kunt voltooide OTA-installaties bekijken op de pagina van de unit. Open de unit en ga naar het tabblad Campagnes om de volledige installatiesgeschiedenis te zien.',
        },
        question_6: {
          title: 'Kan slechte connectiviteit een OTA-installatie beïnvloeden?',
          body: 'Ja. Een OTA-installatie kan mislukken als de unit een onstabiele of geen mobiele of Wi-Fi-verbinding heeft. Zorg ervoor dat de unit een betrouwbare verbinding heeft en verbonden blijft totdat de installatie is voltooid.',
        },
        question_7: {
          title: 'Waarom moet mijn unit regelmatig verbonden zijn?',
          body: 'Regelmatige connectiviteit helpt ervoor te zorgen dat uw unit klaar is voor software-updates. Als een unit enige tijd niet is verbonden, kunnen vereiste gegevens of voorbereidingen ontbreken, wat een OTA-installatie kan vertragen of blokkeren.',
        },
        question_8: {
          title: 'Wat gebeurt er als ik een campagne niet op tijd voltooi?',
          body: 'Campagnes worden niet automatisch geïnstalleerd. Als een campagne niet binnen de opgegeven termijn wordt voltooid, is het mogelijk niet langer mogelijk om deze uit te voeren. Volg altijd de weergegeven instructies en voltooi de campagne binnen de aangegeven periode.',
        },
      },
      maintenance_service: {
        title: 'Onderhoud & service',
        question_1: {
          title: 'Kan ik de onderhoudsgeschiedenis van mijn unit zien?',
          body: 'Ja. Elke unit heeft een tabblad Onderhoudsgeschiedenis waar voltooide services worden weergegeven. Alleen services uitgevoerd door erkende Volvo Penta-dealers worden getoond. Als een service ontbreekt, neem dan rechtstreeks contact op met uw dealer.<br /><br />Selecteer een unit en open het tabblad Onderhoudsgeschiedenis om de details te bekijken.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Overzicht units' },
        },
        question_2: {
          title: 'Waarom kan ik niet zien welke dealer een service heeft uitgevoerd?',
          body: 'Dealergegevens worden alleen weergegeven aan geverifieerde eigenaren van de unit.<br /><br />Om de naam en locatie van de dealer voor een voltooide service te bekijken, moet u geregistreerd zijn als eigenaar. Als u nog niet geverifieerd bent, neem dan contact op met uw erkende Volvo Penta-dealer om uw eigendom te bevestigen.',
        },
      },
      warranty_coverage: {
        title: 'Garantie & uitgebreide dekking',
        question_1: {
          title: 'Hoe weet ik of mijn unit(s) enige garantie & dekking hebben?',
          body: 'Elke unit heeft een tabblad Garantie & dekking waar eventuele actieve garanties worden weergegeven. Selecteer een unit en open het tabblad om de details te bekijken.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Overzicht units' },
        },
        question_2: {
          title: 'Wat is de standaardgarantie?',
          body: 'Alle Volvo Penta-producten hebben een fabrieksgarantie. De dekking geldt voor een bepaalde periode of een bepaald aantal bedrijfsuren, afhankelijk van wat het eerst komt, en is afhankelijk van het product en de toepassing.',
        },
        question_3: {
          title: 'Wat is de standaard onbeperkte uren garantie?',
          body: 'Sommige installatietypen hebben geen limiet voor bedrijfsuren tijdens het eerste en/of tweede jaar van de garantieperiode. Details zijn afhankelijk van het product en de installatie en worden gedefinieerd in de garantievoorwaarden.',
        },
        question_4: {
          title: 'Wat is componentgarantie?',
          body: 'Componentgarantie dekt specifieke belangrijke componenten van uw Volvo Penta-product. De duur van de dekking en de limieten voor bedrijfsuren variëren per component en worden gedefinieerd in de garantievoorwaarden.',
        },
        question_5: {
          title: 'Wat is emissiegarantie?',
          body: 'Motoren die zijn goedgekeurd volgens de US EPA- of California ARB-regelgeving omvatten emissiegarantiedekking. Dit geldt voor een bepaalde periode of een aantal bedrijfsuren, zoals vermeld in de emissiegarantievoorwaarden.',
        },
      },
      notification: {
        title: 'Meldingen',
        question_1: {
          title: 'Waarom ontvang ik geen meldingen over mijn unit(s)?',
          body: 'Controleer uw meldingsinstellingen. Zorg ervoor dat meldingen zijn ingeschakeld op de pagina Instellingen.',
          link: { href: 'https://www.volvopenta.com/connect/settings', label: 'Instellingen' },
        },
      },
    },
    login: {
      hero_title: 'Welkom bij Volvo Penta Connect',
      hero_body:
        'Alles over uw Volvo Penta-motor of -product op één plek. Handleidingen, handboeken, reserveonderdelen, terugroepacties, kwaliteitscampagnes, garantiestatus en meer.',
      log_in: 'Inloggen',
      blurb_back_button: 'Startpagina',
      blurb_bolded_main_text:
        'Welkom bij Volvo Penta Connect, het nieuwe platform voor vlooteigenaren en recreatieve eigenaren.',
      blurb_regular_main_text:
        'Registreer uw motor om toegang te krijgen tot relevante informatie zoals handleidingen, terugroepacties en garantiestatus. Registratie zorgt ervoor dat uw lokale dealer klaarstaat om u te ondersteunen met de juiste onderdelen en services. En het beste van alles: het is helemaal gratis.',
      CTAs: {
        manuals_label: 'Handige toegang tot handleidingen',
        manuals_title: 'Al uw handleidingen met één klik',
        manuals_body:
          'Met Volvo Penta Connect hoeft u niet langer naar het serienummer te zoeken om handleidingen te vinden. Ons platform biedt een one-click-oplossing waarmee u de volledige collectie handleidingen en handboeken voor al uw motoren en producten krijgt.',
        efficiency_label: 'Efficiëntie',
        efficiency_title: 'Vind snel de juiste reserveonderdelen',
        efficiency_body:
          'Volvo Penta Connect biedt een gebruiksvriendelijke functie die het zoeken naar het juiste reserveonderdeel voor uw Volvo Penta-motor vereenvoudigt – zonder dat u het serienummer of het motortype hoeft op te zoeken.',
        service_label: 'Productonderhoud volgen',
        service_title: 'Bekijk de geregistreerde servicegeschiedenis voor uw producten',
        service_body:
          'Blijf op de hoogte van de staat van uw producten met Volvo Penta Connect. Krijg een volledig overzicht, inclusief servicedetails, van uw geregistreerde servicegeschiedenis voor al uw Penta-producten.',
      },
      cards: {
        uptime_label: 'Zorg voor uptime',
        uptime_title: 'Ontdek of u actieve campagnes hebt',
        uptime_body:
          'Met Volvo Penta Connect is het bijhouden van uw productcampagnes nog nooit zo eenvoudig geweest. Zorg dat uw producten up-to-date zijn en altijd optimaal presteren.',
        connect_label: 'Het bijhouden van al uw garanties',
        connect_title: 'Uw garanties in Volvo Penta Connect',
        connect_body:
          'Krijg een overzicht van al uw garanties en dekkingen. Controleer of ze actief zijn en hoe lang ze nog geldig zijn, en ontvang meldingen wanneer ze bijna verlopen.',
      },
    },
    welcome: {
      title: 'Welkom!',
      body: 'Om Volvo Penta Connect volledig te benutten, moet u een vaartuig of machine aan uw account registreren. Zodra deze is toegevoegd, krijgt u toegang tot handleidingen en gidsen, gemakkelijker toegang tot relevante reserveonderdelen, garantiestatussen en nog veel meer.',
      get_help: 'Hulp krijgen',
      get_started: 'Aan de slag',
      accordion: {
        who_is_it_for: {
          title: 'Voor wie is Volvo Penta Connect?',
          body: 'Volvo Penta Connect is een klantenportaal waar u een overzicht krijgt van uw eenheid(en) en relevante informatie zoals handleidingen en gidsen, campagnes, garantiestatus en meer.',
        },
        how_to_register: {
          title: 'Kan ik dit product gebruiken zonder een eenheid te registreren?',
          body: 'U moet een eenheid aan uw account registreren. Zodra een eenheid is toegevoegd, kunt u eenheidsgegevens monitoren, over-the-air software-updates installeren en toegang krijgen tot handleidingen en gidsen die aan die eenheid zijn gekoppeld.',
        },
        can_i_use_without_registering: {
          title: 'Waar kan ik handleidingen en gidsen vinden?',
          body: 'Handleidingen en gidsen worden beschikbaar zodra een eenheid aan uw account is toegevoegd, zodat we documentatie kunnen tonen die overeenkomt met uw specifieke eenheid.',
        },
      },
    },
  },
  components: {
    modal: {
      accept_invitation_modal: {
        invite_title: 'Welkom bij {{name}}!',
        invite_description:
          'Je bent uitgenodigd voor de werkruimte {{fleetName}}. Hier kun je de gezondheid van de vloot en eenheden monitoren, analyses bijhouden en meldingen ontvangen over updates van campagnes en garanties.',
        accept: 'Uitnodiging accepteren',
        dismiss: 'Afwijzen',
      },
      add_units_fleet_modal: {
        button_add: 'Vloot creëren',
        button_update: 'Vloot bijwerken',
        title_add: 'Vloot creëren',
        title_update: 'Vloot bijwerken',
        title_success: 'Vloot succesvol aangemaakt',
        text_success: 'Uw vloot is aangemaakt. Als de nieuwe vloot niet in de lijst verschijnt, ververs dan de pagina.',
        fleet_name: 'Voer de naam van de vloot in of selecteer uw vloot',
        select_your_fleet: 'Selecteer uw vloot',
        click_to_create_fleet: 'Klik om een vloot te creëren',
        table: {
          unit_name: 'Unitnaam',
          location: 'Locatie',
          engines: 'Motoren',
          fleets: 'Vloten',
        },
        cancel: 'Voor nu overslaan',
        submit: {
          add_units: 'Unit(s) aan vloot toevoegen',
          create_fleet: 'Vloot creëren',
          close: 'Sluiten',
        },
      },
      remove_units_fleet_modal: {
        button_remove: 'Verwijderen uit vloot',
        title_remove: 'Eenheden uit vloot verwijderen',
        description:
          'U staat op het punt de geselecteerde eenheden uit de vloot te verwijderen. Deze actie kan niet ongedaan worden gemaakt.',
        confirmation: 'Weet u zeker dat u de geselecteerde eenheden uit de vloot wilt verwijderen?',
        cancel: 'Annuleren',
        submit: {
          create_fleet: 'Verwijderen uit vloot',
        },
      },
      delete_fleet_modal: {
        title: 'Vloot verwijderen',
        description:
          'Het verwijderen van een vloot uit uw account is permanent. U kunt op elk moment opnieuw een vloot toevoegen.',
        confirmation: 'Weet u zeker dat u de volgende vloot wilt verwijderen?',
        submit: 'Ja, vloot uit account verwijderen',
      },
      edit_fleet_modal: {
        title: 'Vloot bewerken',
        fleet_name: 'Vlootnaam',
        select_icon: 'Vlootpictogram selecteren',
      },
      add_new_unit_modal: {
        button: 'Eenheid toevoegen',
        search_unit: {
          title: 'Vind je unit',
          description: 'Zoek de unit die je wilt toevoegen met het motorserienummer.',
          serial_number: 'Motorserienummer',
          find_serial_number: 'Hoe vind je je serienummer',
          is_not_found: {
            label: 'Serienummer bestaat niet in onze administratie',
            body: 'Zorg ervoor dat je het juiste serienummer hebt gevonden. Als het nog steeds niet werkt, neem dan contact op met je Volvo Penta-dealer of zoek er een in onze <a>Dealer Locator</a> om hulp te krijgen.',
          },
          is_not_registered_industrial: {
            label: 'Unit niet geregistreerd voor dit product',
            body: 'Registreer de unit om deze aan je account toe te voegen. De registratieaanvraag wordt behandeld door ons supportteam en de unit wordt toegevoegd nadat de registratie is voltooid.',
          },
          is_not_registered_marine: {
            label: 'Unit niet geregistreerd voor dit product',
            body: 'De unit moet worden geregistreerd voordat je deze aan je account kunt toevoegen. Neem contact op met je Volvo Penta-dealer of zoek er een in onze <a>Dealer Locator</a> voor hulp bij het registreren van de unit.',
          },
          is_maximum_units_reached: {
            label: 'Maximum aantal units bereikt',
            body: 'Je kunt maximaal drie niet-eigendom units toevoegen. Om er meer toe te voegen, moet je geregistreerd zijn als eigenaar van de unit. Neem contact op met je Volvo Penta-dealer voor hulp, of gebruik onze Dealer Locator om er een bij jou in de buurt te vinden.',
          },
          is_unit_already_added: {
            label: 'Unit al toegevoegd aan je account',
            body: 'Deze unit is al beschikbaar in je account en hoeft niet opnieuw geregistreerd te worden. Je kunt het vinden in je unitlijst.',
          },
        },
        unit_information: {
          title: 'Voer unitinformatie in',
          description:
            'Vul de onderstaande gegevens in om je vaartuig toe te voegen aan Volvo Penta Connect en relevante informatie over je Penta-product te ontvangen.',
          privacy_information:
            'Volvo Penta registreert en gebruikt bepaalde persoonsgegevens over u. Als u meer wilt weten over hoe Volvo Penta uw persoonsgegevens verwerkt, ga dan naar <a>Privacy-informatie voor registratie van eigenaar of exploitant</a>.',
        },
        marine_unit_information: {
          serial_number: 'Motorserienummer',
          serial_number_helperText: 'Het Volvo Penta-serienummer staat op de motorplaat.',
          vessel_name: 'Vaartuignaam',
          vessel_name_helperText: 'De naam (bijv. Black Pearl) van je vaartuig.',
          vessel_id: 'Vaartuig-ID',
          imo_number: 'IMO-nummer',
          imo_number_helperText: 'Internationaal Maritiem Organisatienummer dat je vaartuig uniek identificeert.',
          mmsi_number: 'MMSI-nummer',
          mmsi_number_helperText: 'Maritiem Mobiel Service Identiteitsnummer voor radiocommunikatie.',
          manufacturer: 'Fabrikant',
          manufacturer_helperText: 'De fabrikant van je vaartuig.',
          model: 'Model',
          model_helperText: 'Het model van het vaartuig.',
          vessel_segment: 'Vaartuigsegment',
          type_of_vessel: 'Type vaartuig',
          home_port: 'Thuishaven',
          home_port_helperText: 'Locatie of thuishaven van je vaartuig',
          add_unit: 'Unit toevoegen',
        },
        marine_unit_added: {
          title: 'Je unit is toegevoegd',
          description:
            'Je vaartuig is toegevoegd en je kunt nu de informatie van je vaartuig bekijken, handleidingen downloaden, reserveonderdelen vinden en meer.',
          add_another_unit: 'Nog een unit toevoegen',
          go_to_unit: 'Ga naar unit',
        },
        industrial_contact_information: {
          title: 'Voer contactinformatie in',
          description:
            'Vul de onderstaande eigenaarsinformatie in om je unit toe te voegen. Je ingevoerde gegevens worden handmatig gecontroleerd door ons supportteam voordat je als eigenaar wordt geregistreerd.',
          proof_of_ownership: 'Bewijs van eigendom',
          proof_of_ownership_helperText:
            'Een bewijs van eigendom (bijv. contract, ontvangstbewijs of orderbevestiging) is vereist om het bedrijf te registreren als eigenaar van de machine. Als je geen eigendomsbewijs kunt leveren, neem dan contact op met je dealer.',
          proof_of_ownership_upload:
            'Sleep en zet neer of klik om te bladeren naar bestanden, PDF, png, jpg, of heic bestanden van maximaal 3mb per stuk.',
          proof_of_ownership_qr: 'Eigendom bevestigd door QR-code',
          contact_person: 'Contactpersoon',
          contact_person_description: 'Je wordt geregistreerd als contactpersoon voor het bedrijf en deze machine.',
          first_name: 'Voornaam',
          last_name: 'Achternaam',
          email: 'E-mail',
          company_details: 'Bedrijfsgegevens',
          company_details_description: 'Voer de informatie in van het bedrijf dat eigenaar is van de machine.',
          company_name: 'Bedrijfsnaam',
          company_name_helperText: 'Het bedrijf dat eigenaar is van de machine.',
          vat_number: 'BTW-nummer',
          address_line: 'Adres',
          zip_code: 'Postcode',
          city: 'Stad',
          state: 'Staat / Provincie',
          country: 'Land',
          notification: 'Meldingsvoorkeuren',
        },
        industrial_unit_information: {
          engine_serial_number: 'Motorserienummer',
          engine_serial_number_helperText:
            'Als je machine meer dan één motor heeft, klik dan om een extra veld toe te voegen en voer het serienummer van de extra motor in.',
          add_engine: 'Motor toevoegen',
          machine_name: 'Machinenaam',
          machine_name_helperText: 'De naam (bijv. T4, Unit 11) van je machine, voertuig of stroomgenerator.',
          machine_id: 'Machine-ID',
          machine_id_helperText: 'Het unieke identificatienummer verstrekt door de originele fabrikant (OEM).',
          manufacturer: 'Fabrikant',
          manufacturer_helperText:
            'De fabrikant van je machine, voertuig of stroomgenerator (bijv. Magni, Inmesol, Atlas).',
          model: 'Model',
          model_helperText: 'Het model van je machine, voertuig of stroomgenerator.',
          machine_segment: 'Machinesegment',
          machine_segment_helperText:
            'Het segment waarin je machine zich bevindt (bijv. materiaalbehandeling, mijnbouw, bosbouw).',
          type_of_machine: 'Type machine',
          type_of_machine_helperText: 'Wat voor type machine het is (bijv. terminaltrekker, harvester, vorkheftruck).',
          operating_location: 'Operationele locatie',
          operating_location_helperText: 'Locatie of operationele locatie van je machine.',
        },
        industrial_unit_added: {
          title: 'Unit toegevoegd',
          description:
            'Je machine is toegevoegd en je kunt nu de informatie van je machine bekijken, handleidingen downloaden, reserveonderdelen vinden en meer.',
          manual_ownership_registration: 'Handmatige eigendomsregistratie',
          manual_ownership_registration_description:
            'Registratie en eigendomscontrole worden handmatig uitgevoerd door ons supportteam. <b>Momenteel hebben we te maken met een hoge vraag, wat leidt tot lange verwerkingstijden, verwacht tussen 2-6 weken.</b> Je ontvangt een bevestigingsmail wanneer je aanvraag is goedgekeurd.',
          add_another_unit: 'Nog een unit toevoegen',
          go_to_unit: 'Ga naar unit',
        },
        industrial_no_unit_added: {
          title: 'Machine wordt niet weergegeven in Volvo Penta Connect',
          description:
            'Je machine bestaat niet in onze administratie. Het zal niet in Volvo Penta Connect worden weergegeven totdat het handmatige registratieproces door ons supportteam is voltooid.',
          manual_registration: 'Handmatige registratie',
          manual_registration_description:
            'Registratie en eigendomscontrole worden handmatig uitgevoerd door ons supportteam. <b>Momenteel hebben we te maken met een hoge vraag, wat leidt tot lange verwerkingstijden, verwacht tussen 2-6 weken.</b> Je ontvangt een bevestigingsmail wanneer je aanvraag is goedgekeurd.',
          manuals_campaigns: 'Handleidingen, onderdelen en campagnes',
          manuals_campaigns_description:
            'Als je op zoek bent naar handleidingen en handboeken, reserveonderdelen of campagnes - hier zijn links waar je het kunt vinden totdat de registratie is voltooid. Je hebt deze informatie ook ontvangen in je e-mailinbox.',
          add_another_unit: 'Nog een unit toevoegen',
        },
      },
      invite_members_modal: {
        button: 'Leden toevoegen',
        title: 'In afwachting van uitnodigingslinks',
        empty_state: {
          title: 'Geen uitnodigingen gevonden.',
          message: 'Probeer met de onderstaande knop een uitnodiging aan te maken en naar uw klembord te kopiëren.',
        },
        generate_invitation_link: 'Nieuwe uitnodigingslink genereren en naar klembord kopiëren',
      },
      remove_members_modal: {
        title: 'Lid/leden verwijderen',
        description:
          'Het verwijderen van een lid uit het {{count}}-account is permanent. U kunt een lid op elk moment opnieuw uitnodigen.',
        are_you_sure: 'Weet u zeker dat u de volgende lid/leden wilt verwijderen?',
        cancel: 'Annuleren',
        submit: 'Ja, lid/leden uit account verwijderen',
      },
      add_network_modal: {
        button: 'Netwerk toevoegen',
        title: 'Netwerk toevoegen',
        refresh_network: 'Netwerk vernieuwen',
        edit_network: 'Netwerk bewerken',
        remove_network: 'Netwerk verwijderen',
        remove_network_description:
          'Verwijder het netwerk uit uw opgeslagen lijst voor dit voertuig / vaartuig. Het verwijderen van het netwerk is permanent. U kunt het netwerk altijd opnieuw toevoegen.',
        network_name: 'Netwerknaam (SSID)',
        network_name_helperText: 'Netwerk-ID van de wifi-router',
        password: 'Wachtwoord',
        password_helperText: 'Wachtwoord van de wifi-router',
        error_title: 'Er is iets misgegaan',
        error_description: 'Het netwerk kon niet worden bijgewerkt. Probeer het later opnieuw.',
        priority: 'Prioriteit',
      },
      wifi_polling_modal: {
        title: 'Updating Network Settings',
        title_error: 'Network Settings Update',
        connecting: 'Connecting to your device...',
        searching: 'Searching for your network settings...',
        almost: 'Almost there, fetching configuration...',
        hang_tight: 'Still working on it, hang tight...',
        communicating: 'Communicating with the gateway...',
        moment: 'Just a moment longer...',
        timeout: 'Unable to reach your device. Please try again later.',
        error: 'Something went wrong while updating network settings. Please try again.',
      },
      wifi_error_log_modal: {
        title: 'Foutenlog',
        no_errors: 'Geen fouten gevonden',
        close: 'Sluiten',
      },
      digital_services_modal: {
        activate_service_content: {
          title: 'Service activeren',
          description:
            'Geef aan namens wie u deze service activeert, zodat wij het juiste activeringscontract kunnen voorbereiden en de juiste contactgegevens kunnen opvragen.',
          myself: 'Mijzelf',
          myself_helper: 'Activeer deze service voor uw persoonlijke machine of vaartuig.',
          company: 'Een bedrijf',
          company_helper: 'Activeer deze service namens een bedrijf, vloot of organisatie.',
        },
        contract_details_content: {
          title: 'Contactgegevens',
          first_name: 'Voornaam',
          last_name: 'Achternaam',
          email_address: 'E-mailadres',
          company_name: 'Bedrijfsnaam',
          company_details_title: 'Bedrijfsgegevens',
          company_details_description: 'Voer de gegevens in van het bedrijf dat eigenaar is van de machine.',
          vat_number: 'BTW-nummer',
          social_security_number: 'Sociale zekerheidsnummer',
          state_province: 'Staat/provincie',
          country: 'Land',
          address: 'Adres',
          city: 'Stad',
          postal_code: 'Postcode',
          is_correct_info: 'Hierbij bevestig ik dat de bovenstaande informatie waarheidsgetrouw en correct is.',
          is_terms_checked:
            'Ik erken en bevestig dat ik de algemene voorwaarden van de Overeenkomst Digitale Diensten (inclusief bijlagen) heb gelezen en begrepen en dat ik bevoegd ben om genoemde <a>overeenkomst</a> te accepteren met rechtsgeldige werking.',
          data_management_agreement: 'Ik heb ook de <a>Gegevensbeheerovereenkomst</a> gelezen en begrepen.',
          remote_software_updates_title: 'Remote Software Updates',
          service_description_for: 'Servicebeschrijving voor {{serviceName}}',
          is_service_details_checked:
            'Hierbij erken en bevestig ik dat ik de <serviceDescription /> heb gelezen en begrepen en dat ik bevoegd ben om genoemde servicegegevens te accepteren.',
          is_data_act_checked:
            'De EU Data Act vereist dat wij u precontractuele informatie verstrekken waarin wordt beschreven hoe een Volvo Penta Connected Product (op de markt gebracht in een EU-lidstaat) productgegevens genereert, hoe de productgegevens worden verzameld, hoe u als gebruiker toegang kunt krijgen tot deze gegevens en hoe deze door Volvo Penta worden gebruikt. De volledige informatie is beschikbaar op <a>EU Data Act | Volvo Penta</a>. Door dit vakje aan te vinken en door te gaan met de aankoop, lease of huur van het Volvo Penta Connected Product, erkent u dat u toegang hebt gekregen tot deze precontractuele informatie zoals vereist door de EU Data Act.',
        },
        choose_receiver_content: {
          title: 'Kies een dealer',
          description:
            'Voeg een dealer toe om de meldingen van waarschuwingen te ontvangen. Dit zorgt voor maximale uptime en de meest efficiënte serviceplanning voor uw Volvo Penta-product.',
          find_nearest_dealer: 'Vind uw dichtstbijzijnde dealer',
          dealer: 'Dealer',
          dealer_placeholder: 'Voer de naam van de dealer en/of hun e-mailadres in',
        },
        thanks_content: {
          title: 'Dank u!',
          description:
            'Houd er rekening mee dat activering niet onmiddellijk plaatsvindt en enige tijd in beslag zal nemen. We sturen u een e-mail wanneer de service gereed is voor gebruik.',
          description_all_services: 'We sturen u een e-mail zodra de services gereed zijn voor gebruik.',
          Marine: {
            description:
              'Houd er rekening mee dat activering niet onmiddellijk plaatsvindt en enige tijd in beslag kan nemen. We sturen u een e-mail zodra de activering is voltooid.',
          },
        },
        deactivate_service: {
          title: 'Service beëindigen',
          description: 'Weet u zeker dat u de service wilt beëindigen?',
          description_all: 'Weet u zeker dat u alle services wilt beëindigen, ({{serviceNames}})?',
          deactivate: 'Beëindigen',
        },
      },
    },
    table: {
      items_selected: '{{selectedRowsCount}} items geselecteerd',
      page: 'Pagina {{currentPage}} van {{totalPages}}',
      unit_table: {
        unit_name: 'Unitnaam',
        model_brand: 'Model/merk',
        location: 'Locatie',
        engines: 'Motoren',
        operating_hours: 'Bedrijfsuren',
        fleets: 'Vloten',
        campaigns: 'Campagnes',
        digital_services: 'Digitale diensten',
        unverified_ownership: 'Onverifieerd eigendom',
      },
      campaigns_table: {
        campaign: 'Campagne',
        download: 'Downloaden',
        created_date: 'Aanmaakdatum',
        valid_to: 'Geldig tot',
        product: 'Product',
        type: 'Type',
      },
      members_table: {
        name: 'Naam',
        email: 'E-mail',
        account: 'Account',
      },
      invitations_table: {
        creation_date: 'Aanmaakdatum',
      },
      specifikationer_table: {
        driveline: 'Aandrijflijn',
        product_type: 'Producttype',
        product: 'Product',
        serial_number: 'Serienummer',
        operating_hours: 'Bedrijfsuren',
        engine: 'Motor',
        segment: 'Segment',
        rated_power: 'Nominaal vermogen',
        rated_speed: 'Nominaal toerental',
      },
      network_settings_table: {
        priority: 'Prioriteit',
        ssid: 'SSID',
        frequency: 'Frequentie',
        last_updated: 'Laatst bijgewerkt',
        connection: 'Verbinding',
        connected: 'Verbonden',
        not_connected: 'Niet verbonden',
        lost_connection: 'Verbinding verbroken',
        disconnected: 'Verbroken',
      },
      coverage_table: {
        type: 'Type',
        description: 'Garantie-/dekkingsomschrijving',
        expiration_hours: 'Vervaluren',
        expiration_date: 'Vervaldatum',
      },
      fault_codes_table: {
        unit: 'Eenheid',
        severity: 'Ernst',
        fault_code: 'Foutcode',
        fault_description: 'Foutbeschrijving',
        failure_event: 'Faalgebeurtenis',
        timestamp: 'Tijdstempel',
        operating_hours: 'Bedrijfsuren',
        occurrences: 'Voorvallen',
      },
      connectivity_table: {
        unit: 'Eenheid',
        operating_hours: 'Bedrijfsuren',
        last_seen: 'Laatst gezien',
        status: 'Connectiviteitsstatus',
      },
      fuel_consumption_table: {
        unit: 'Eenheid',
        chassis_id: 'Chassis-ID',
        operating_hours: 'Bedrijfsuren (u)',
        fuel_efficiency: 'Brandstofefficiëntie (l/u)',
        total_fuel: 'Totaal brandstof (l)',
      },
      publications_table: {
        title: 'Titel',
        description: 'Beschrijving',
        designation: 'Product',
        product: 'Producttype',
        size: 'Grootte',
        languageCode: 'Taal',
        buy: 'Kopen',
      },
      maintenance_dealer_table: {
        header: 'Dealership',
        dealer_name: 'Dealer naam',
        location: 'Locatie',
      },
      maintenance_protocol_details_table: {
        header: 'Serviceprotocoldetails',
        title: 'Titel',
        current_service: 'Huidige service',
        previous_service: 'Vorige service',
        date: 'Datum',
        operating_hours: 'Bedrijfsuren',
        unit_age: 'Leeftijd van de eenheid',
        months: 'maanden',
      },
      maintenance_driveline_table: {
        header: 'Aandrijflijn {{driveline}}',
        name: 'Naam',
        serial_number: 'Serienummer',
        product: 'Product',
      },
      maintenance_service_table: {
        header: 'Service {{service}}',
        activity: 'Activiteit',
        detail: 'Detail',
        last_performed: 'Laatst uitgevoerd',
        performed: 'Uitgevoerd',
        complete: 'Voltooid',
        not_performed: 'Niet uitgevoerd',
      },
    },
    tooltip: {
      copy_clipboard: 'Kopiëren naar klembord',
    },
    language_selector: {
      de: 'Sprache - Deutsch',
      en: 'Language - English',
      fr: 'Langue - Français',
      it: 'Lingua - Italiano',
      nl: 'Taal - Nederlands',
      pt: 'Idioma - Português',
      sv: 'Språk - Svenska',
    },
    main_menu: {
      home: 'Startpagina',
      settings: 'Instellingen',
      support: 'Support',
    },
    user_menu: {
      title: 'Gebruikersmenu',
      account_settings: 'Accountinstellingen',
      logout: 'Uitloggen',
      switch_fleet_account: 'Van vlootaccount wisselen',
    },
    footer: {
      useful: 'Handig',
      manuals_and_handbooks: 'Handleidingen & handboeken',
      dealer_locator: 'Dealerzoeker',
      warranty: 'Garantie',
      parts_column: 'Onderdelen en accessoires',
      parts: 'Onderdelen',
      remanufactured: 'Gereviseerde componenten en motoren',
      contact: 'Contact',
      action_service: 'Volvo Penta Action Service',
      faq: 'Veelgestelde vragen',
      contact_us: 'Neem contact met ons op',
      privacy_policy: 'Privacybeleid',
      cookie_policy: 'Cookiebeleid',
    },
    cta: {
      members_fleetaccount_cta: {
        heading: 'Nodig anderen uit voor uw account',
        message:
          "Voeg leden toe om toegang te delen tot uw eenheden en vloot. U kunt collega's, zakenpartners of familieleden uitnodigen en rollen toewijzen om te bepalen wat ze kunnen zien en beheren. Kopieer de link en stuur deze naar uw lid.",
      },
      members_digitalaccount_cta: {
        heading: 'Nodig anderen uit voor uw account',
        message:
          "Het uitnodigen van leden vereist een geverifieerde eigenaar van de eenheid. Zodra uw eigendom is bevestigd, kunt u collega's, zakenpartners of familieleden uitnodigen en rollen toewijzen om te bepalen wat ze kunnen zien en beheren.<br /><br />Om eigendom te verifiëren, neem contact op met uw erkende Volvo Penta-dealer.",
      },
      action_cta: {
        heading: 'Hoe te handelen',
        message: 'Om een campagne uit te voeren, neem contact op met uw Volvo Penta- of OEM-dealer.',
        find_dealer: 'Vind uw dichtstbijzijnde dealer',
        learn_more: 'Meer informatie',
      },
      notifications_cta: {
        heading: 'Notificaties',
        message:
          'Blijf op de hoogte en ontvang e-mailmeldingen wanneer een eenheid een installeerbare campagne beschikbaar heeft.',
        notification_settings: 'Meldingsinstellingen',
      },
    },
    role_types: {
      title: 'Rolinformatie',
      manager: {
        title: 'Manager',
        description:
          'Managers hebben volledige administratieve controle om de vloot te beheren, de status van units te bewaken en alle units, instellingen en gebruikerstoegang te beheren.',
      },
      technician: {
        title: 'Technician',
        description:
          'Richt zich op de status en het onderhoud van units. Technici kunnen unitgegevens bewaken en software-updates op afstand uitvoeren. Deze rol omvat geen rechten voor vloot- of gebruikersbeheer.',
      },
      viewer: {
        title: 'Viewer',
        description:
          'Viewers hebben alleen-lezen toegang. Ze kunnen de unitstatus bewaken en rapporten bekijken, maar kunnen geen instellingen of configuraties wijzigen.',
      },
    },
    campaign_types: {
      title: 'Inzicht in campagnetypen',
      recall: {
        title: 'Terugroepactie',
        description:
          'Retroactieve invoering van een productiewijziging; moet bij de eerstvolgende gelegenheid worden uitgevoerd om een juiste werking van het product te garanderen.',
      },
      quality: {
        title: 'Kwaliteit',
        description:
          'Retroactieve invoering van een productiewijziging; moet bij de volgende geplande service worden uitgevoerd om een juiste werking van het product te garanderen.',
      },
      service: {
        title: 'Service',
        description:
          'Veiligheidsgerelateerde retroactieve invoering van een productiewijziging; moet onmiddellijk worden uitgevoerd om een veilige werking van het product te garanderen.',
      },
      installation: {
        title: 'Installatie',
        description:
          'Een externe software-update is klaar om door u op uw unit te worden geïnstalleerd met de Volvo Penta-applicatie.',
        app_store: 'Download voor iOS in de App Store',
        google_play: 'Download voor Android op Google Play',
      },
    },
  },
};

export default nlTranslation;
